import { useMemo, useState, useEffect } from "react";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import { DOMAIN_API_HOST } from "../../../configs/main";

import "./mediaPlan.scss";
import "../styles/company.css";

import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Modal, Box } from "@material-ui/core";

import {
  createNewList,
  updateCampaign,
} from "../../../redux/createNewCompany/actions";
import { enqueueSnackbar } from "../../../redux/notifications/actions";
import RangeDaysPicker from "../../../modules/RangeDaysPicker/RangeDaysPicker";
import { DateModel } from "../../../modules/RangeDaysPicker/dateHelper";
import TooltipedButton from "../../../ui/TooltipedButton/TooltipedButton";
import { setInitialValue } from "../../../utils/helpers";
import { LoadingButton } from "@mui/lab";

const styles = {
  maxHeight: "90vh",
  overflowY: "auto",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "fit-content",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "18px",
  boxShadow: "0 8px 28px rgba(20, 23, 31, 0.10)",
  padding: "0",
  outline: "none",
};

const DOMAIN_LABEL = "[a-zа-яё0-9](?:[a-zа-яё0-9-]{0,61}[a-zа-яё0-9])?";
const DOMAIN_VALIDATION = new RegExp(
  `^(?:${DOMAIN_LABEL}\\.)+[a-zа-яё]{2,63}$`,
  "i"
);

const normalizeDomain = (raw) => {
  const withoutProtocol = raw.trim().toLowerCase().replace(/^https?:\/\//, "");
  const host = withoutProtocol.split(/[/?#]/)[0];
  return host.replace(/^[^@]*@/, "").replace(/:\d*$/, "");
};

const REJECTED_PREVIEW_LIMIT = 5;

const CloseIcon = ({ onClick, className }) => (
  <svg
    className={className}
    onClick={onClick}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    viewBox="0 0 256 256"
  >
    <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
  </svg>
);

const DomainsHintBanner = ({ isPartial }) => {
  const [open, setOpen] = useState(false);
  const label = isPartial ? "тексты" : "домены";

  return (
    <div className={`wbc-hint ${open ? "wbc-hint--open" : ""}`}>
      <button
        type="button"
        className="wbc-hint__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>Как добавлять {label}</span>
        <svg
          className="wbc-hint__chevron"
          width="14"
          height="14"
          viewBox="0 0 256 256"
          fill="currentColor"
        >
          <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" />
        </svg>
      </button>
      {open && (
        <ul className="wbc-hint__list">
          <li>Можно указать несколько значений через пробел и нажать Enter</li>
          {isPartial ? (
            <>
              <li>Минимум 3 символа, максимум 255</li>
              <li>
                Подойдёт: <code>новости</code>, <code>sport</code>
              </li>
              <li>
                Не подойдёт: <code>ab</code> (слишком коротко)
              </li>
            </>
          ) : (
            <>
              <li>Домен должен содержать зону (например, .ru, .com)</li>
              <li>
                Ссылку можно вставить целиком — сохранится только домен:{" "}
                <code>https://lk.site.ru/add-company</code> →{" "}
                <code>lk.site.ru</code>
              </li>
              <li>
                Подойдёт: <code>example.com</code>,{" "}
                <code>https://sub.site.ru</code>
              </li>
              <li>
                Не подойдёт: <code>example</code>, <code>site..ru</code>,{" "}
                <code>https://site./ru</code>
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  );
};

const RejectedBanner = ({ rejected, isPartial, onDismiss }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rejected.length) return null;

  const visible = expanded ? rejected : rejected.slice(0, REJECTED_PREVIEW_LIMIT);
  const hiddenCount = rejected.length - REJECTED_PREVIEW_LIMIT;
  const label = isPartial ? "текстов" : "доменов";

  return (
    <div className="wbc-rejected">
      <div className="wbc-rejected__header">
        <span>
          Не добавлено {rejected.length} {label} — не прошли валидацию
        </span>
        <CloseIcon className="wbc-rejected__close" onClick={onDismiss} />
      </div>
      <div className="wbc-rejected__items">
        {visible.map((item) => (
          <span key={item} className="wbc-rejected__chip" title={item}>
            {item}
          </span>
        ))}
      </div>
      {rejected.length > REJECTED_PREVIEW_LIMIT && (
        <button
          type="button"
          className="wbc-rejected__toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Свернуть" : `Показать ещё ${hiddenCount}`}
        </button>
      )}
    </div>
  );
};

const ListChip = ({ item, onRemove }) => (
  <div className="wbc-chip">
    <span className="wbc-chip__text" title={item}>
      {item}
    </span>
    <CloseIcon className="wbc-chip__remove" onClick={() => onRemove(item)} />
  </div>
);

const INITIAL_VALUES = {
  enable_desktop: true,
  enable_mobile: true,
  use_holidays_moving: false,
  date_start: null,
  date_end: null,
  channels: {
    mobile_apps: "a",
    social_networks: "a",
  },
  show_schedules: {},
  max_impressions: 13,
  max_banner_impressions: 13,
  max_impressions_uniq: 1,
};

const normalizeBoolean = (value, fallback) => {
  if (value === undefined || value === null) {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

const DataEqual = (objA, objB) => {
  if (!objA || !objB) return false;
  const properties = [
    'date_start',
    'date_end',
    'max_impressions',
    'max_impressions_uniq',
    'max_banner_impressions',
    'channels',
    'show_schedules',
    'enable_mobile',
    'enable_desktop',
  ];
  for (let key of properties) {
    if (key === 'show_schedules') {
      const schedulesA = objA[key] || {}
      const schedulesB = objB[key] || {}
      const daysA = Object.keys(schedulesA)
      const daysB = Object.keys(schedulesB)
      if (daysA.length !== daysB.length) return false
      for (let day of daysA) {
        const hoursA = schedulesA[day] || []
        const hoursB = schedulesB[day] || []
        if (hoursA.length !== hoursB.length) return false
        for (let i = 0; i < hoursA.length; i++) {
          if (hoursA[i] !== hoursB[i]) return false
        }
      }
    } else if (objA[key] !== objB[key]) {
      return false
    }
  }
  return true
}



const FULL_MATCH_ENDPOINTS = {
  white: "whitelist_domains",
  black: "blacklist_domains",
  concurrent: "competitor_list_domains",
};

const PARTIAL_MATCH_ENDPOINTS = {
  white: "whitelist_partial",
  black: "blacklist_partial",
  concurrent: "competitor_list_partial",
};

const MODAL_TITLES = {
  white: "Белый список",
  black: "Чёрный список",
  concurrent: "Сайты конкурентов",
};

const getAuthHeaders = () => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");
  myHeaders.append(
    "Authorization",
    "Bearer " + localStorage.getItem("token")
  );
  return myHeaders;
};

const saveList = async (campaignId, apiEndpoint, newList) => {
  await fetch(
    `${DOMAIN_API_HOST}/api/v1/marketing/wbc?campaign_id=${campaignId}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ [apiEndpoint]: newList }),
    }
  );
};

const validateDomain = (value, currentList) => {
  if (!value) return "Некорректный домен";
  if (value.length > 253) return "Максимум 253 символа";
  if (!DOMAIN_VALIDATION.test(value)) return "Некорректный домен";
  if (currentList.includes(value)) return "Уже добавлен";
  return null;
};

const validatePartial = (value, currentList) => {
  if (value.length < 3) return "Минимум 3 символа";
  if (value.length > 255) return "Максимум 255 символов";
  if (currentList.includes(value)) return "Уже добавлен";
  return null;
};

const FullMatchList = ({ campaignId, type }) => {
  const [list, setList] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const apiEndpoint = FULL_MATCH_ENDPOINTS[type] || FULL_MATCH_ENDPOINTS.black;

  const addToList = async (input) => {
    const items = input
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!items.length) return;

    const valid = [];
    const invalid = [];
    const seen = new Set(list);

    items.forEach((item) => {
      const domain = normalizeDomain(item);
      if (seen.has(domain) || validateDomain(domain, list)) {
        invalid.push(item);
        return;
      }
      valid.push(domain);
      seen.add(domain);
    });

    setRejected(invalid);
    setInputValue("");

    if (!valid.length) return;

    const newList = [...list, ...valid];
    setList(newList);
    await saveList(campaignId, apiEndpoint, newList);
  };

  const removeFromList = async (item) => {
    const newList = list.filter((i) => i !== item);
    setList(newList);
    await saveList(campaignId, apiEndpoint, newList);
  };

  useEffect(() => {
    async function fetchData() {
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/marketing/wbc?campaign_id=${campaignId}&_type=${apiEndpoint}`,
        { method: "GET", headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setList(data || []);
      }
    }
    fetchData();
  }, [campaignId, apiEndpoint]);

  return (
    <div className="wbc-section">
      <div className="wbc-section__header">
        <h3 className="wbc-section__title">Полное совпадение</h3>
        <span className="wbc-section__count">Добавлено: {list.length}</span>
      </div>

      <DomainsHintBanner isPartial={false} />

      <div className="wbc-input-row">
        <input
          className="wbc-input"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (rejected.length) setRejected([]);
          }}
          placeholder="example.com site.ru — через пробел, Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addToList(e.target.value);
            }
          }}
          type="text"
        />
        <button
          type="button"
          className="wbc-add-btn"
          onClick={() => addToList(inputValue)}
        >
          Добавить
        </button>
      </div>

      <RejectedBanner
        rejected={rejected}
        isPartial={false}
        onDismiss={() => setRejected([])}
      />

      <div className="wbc-list-label">Список доменов</div>
      {list.length === 0 ? (
        <p className="wbc-empty">Пока нет добавленных доменов</p>
      ) : (
        <div className="wbc-chips">
          {list.map((item) => (
            <ListChip key={item} item={item} onRemove={removeFromList} />
          ))}
        </div>
      )}
    </div>
  );
};

const PartialMatchList = ({ campaignId, type }) => {
  const [list, setList] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const apiEndpoint =
    PARTIAL_MATCH_ENDPOINTS[type] || PARTIAL_MATCH_ENDPOINTS.black;

  const addToList = async (input) => {
    const items = input
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!items.length) return;

    const valid = [];
    const invalid = [];
    const seen = new Set(list);

    items.forEach((item) => {
      if (seen.has(item)) {
        invalid.push(item);
        return;
      }
      const error = validatePartial(item, list);
      if (error) {
        invalid.push(item);
      } else {
        valid.push(item);
        seen.add(item);
      }
    });

    setRejected(invalid);
    setInputValue("");

    if (!valid.length) return;

    const newList = [...list, ...valid];
    setList(newList);
    await saveList(campaignId, apiEndpoint, newList);
  };

  const removeFromList = async (item) => {
    const newList = list.filter((i) => i !== item);
    setList(newList);
    await saveList(campaignId, apiEndpoint, newList);
  };

  useEffect(() => {
    async function fetchData() {
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/marketing/wbc?campaign_id=${campaignId}&_type=${apiEndpoint}`,
        { method: "GET", headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setList(data || []);
      }
    }
    fetchData();
  }, [campaignId, apiEndpoint]);

  return (
    <div className="wbc-section">
      <div className="wbc-section__header">
        <h3 className="wbc-section__title">Частичное совпадение</h3>
        <span className="wbc-section__count">Добавлено: {list.length}</span>
      </div>

      <DomainsHintBanner isPartial />

      <div className="wbc-input-row">
        <input
          className="wbc-input"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (rejected.length) setRejected([]);
          }}
          placeholder="текст1 текст2 — через пробел, Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addToList(e.target.value);
            }
          }}
          type="text"
        />
        <button
          type="button"
          className="wbc-add-btn"
          onClick={() => addToList(inputValue)}
        >
          Добавить
        </button>
      </div>

      <RejectedBanner
        rejected={rejected}
        isPartial
        onDismiss={() => setRejected([])}
      />

      <div className="wbc-list-label">Список текстов</div>
      {list.length === 0 ? (
        <p className="wbc-empty">Пока нет добавленных текстов</p>
      ) : (
        <div className="wbc-chips">
          {list.map((item) => (
            <ListChip key={item} item={item} onRemove={removeFromList} />
          ))}
        </div>
      )}
    </div>
  );
};

const OpenModal = ({ openModal, setOpenModal, type, campaignId }) => {
  if (!type || !MODAL_TITLES[type]) return null;

  return (
    <Modal
      open={openModal}
      onClose={() => setOpenModal(false)}
      aria-labelledby="wbc-modal-title"
      aria-describedby="wbc-modal-description"
    >
      <Box sx={{ ...styles, width: "720px", maxWidth: "95vw" }}>
        <div className="wbc-modal">
          <div className="wbc-modal__header">
            <h3 id="wbc-modal-title" className="wbc-modal__title">
              {MODAL_TITLES[type]}
            </h3>
            <button
              type="button"
              className="wbc-modal__close"
              onClick={() => setOpenModal(false)}
              aria-label="Закрыть"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="wbc-modal__body">
            <FullMatchList campaignId={campaignId} type={type} />
            <PartialMatchList campaignId={campaignId} type={type} />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

const PLACEMENT_ICONS = {
  white: (
    <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
      <path d="M208,40H48A16,16,0,0,0,32,56v58.78c0,89.61,75.82,119.34,91,124.39a15.53,15.53,0,0,0,10,0c15.2-5.05,91-34.78,91-124.39V56A16,16,0,0,0,208,40Zm-34.34,69.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z" />
    </svg>
  ),
  black: (
    <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24ZM40,128a87.61,87.61,0,0,1,20.42-56.32l123.9,123.9A88,88,0,0,1,40,128Zm155.58,56.32L71.68,60.42A88,88,0,0,1,195.58,184.32Z" />
    </svg>
  ),
  concurrent: (
    <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
      <path d="M128,56C48,56,8,128,8,128s40,72,120,72,120-72,120-72S208,56,128,56Zm0,112a40,40,0,1,1,40-40A40,40,0,0,1,128,168Zm0-64a24,24,0,1,0,24,24A24,24,0,0,0,128,104Z" />
    </svg>
  ),
};

const PlacementCard = ({ title, description, accent, onClick }) => (
  <div
    className="placement-card"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <div className="placement-card__top">
      <span className="placement-card__icon">{PLACEMENT_ICONS[accent]}</span>
      <svg
        className="placement-card__arrow"
        width="18"
        height="18"
        viewBox="0 0 256 256"
        fill="currentColor"
      >
        <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z" />
      </svg>
    </div>
    <p className="placement-card__title">{title}</p>
    <p className="placement-card__desc">{description}</p>
  </div>
);

const MediaPlan = ({ campaignID, handleChange, data, disable }) => {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [upd, setUpd] = useState(false);

  const [whiteList, setWhiteList] = useState([]);
  const [blackList, setBlackList] = useState([]);
  const [rivalList, setRivalList] = useState([]);
  const [uploadErrors, setErrors] = useState({
    whiteList: '',
    blackList: '',
    rivalList: ''
  })
  const [isSubmit, setIsSubmit] = useState(false)

  // const [range, setRange] = useState([]);
  let timeData = useMemo(() => {
    if (data?.show_schedules) {
      const date = new DateModel().setInitData(data?.show_schedules);
      date.setInitFromto(data?.date_start, data?.date_end);
      return date
    }
    return new DateModel().setInitData();
  }, [data?.show_schedules]);

  const preparedData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      enable_mobile: normalizeBoolean(
        data.enable_mobile,
        INITIAL_VALUES.enable_mobile
      ),
      enable_desktop: normalizeBoolean(
        data.enable_desktop,
        INITIAL_VALUES.enable_desktop
      ),
    };
  }, [data]);

  const handleDatesClear = () => {
    timeData.days = []
    timeData.hours = []
    timeData.fromTime = null
    timeData.toTime = null
    timeData.listSelectedRanges = []
    timeData.selectedRange = []
    timeData.unionDates = []
    timeData.clickedDates = {
      days: { active: [], dates: [] },
      cells: { active: [], dates: [] },
      hours: { active: [], dates: [] }
    }
    setUpd((value) => !value);
  }
  const isEmpty = (obj) => {
    return Object.keys(obj).length === 0;
  };
  const {
    values,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    enableReinitialize: true,
    initialValues: setInitialValue(preparedData, INITIAL_VALUES),

    validate: (values) => {
      let errors = {};
      if (values.date_start > values.date_end) {
        errors.date_start = "Обязательно";
      }
      if (!values.max_impressions) {
        errors.max_impressions = "Обязательно";
      }
      else if ((1 > values.max_impressions) || (values.max_impressions > 31)) {
        errors.max_impressions = "Может быть от 1 до 31";
      }
      if (!values.max_impressions_uniq) {
        errors.max_impressions_uniq = "Обязательно";
      }
      else if (values.max_impressions_uniq <= 0) {
        errors.max_impressions_uniq = "Может быть только положительным";
      }
      if (!values.max_banner_impressions) {
        errors.max_banner_impressions = "Обязательно";
      }
      else if ((1 > values.max_banner_impressions) || (values.max_banner_impressions > 31)) {
        errors.max_banner_impressions = "Может быть от 1 до 31";
      }
      if (!values.channels.mobile_apps) {
        errors.channels_mobile_apps = "Обязательно";
      }
      if (!values.channels.social_networks) {
        errors.channels_social_networks = "Обязательно";
      }
      return errors;
    },

    onSubmit: async (values) => {
      if (!isSubmit) return;
      if (
        !Array.isArray(values.channels.mobile_apps) &&
        !Array.isArray(values.channels.social_networks)
      ) {
        values.channels.mobile_apps = values.channels.mobile_apps.replace(
          /\s+/g,
          ""
        );
        values.channels.social_networks =
          values.channels.social_networks.replace(/\s+/g, "");
        values.channels.mobile_apps = values.channels.mobile_apps.split(",");
        values.channels.social_networks =
          values.channels.social_networks.split(",");
      }

      if (!isEmpty(whiteList)) {
        dispatch(createNewList(whiteList.file.file, "WHITE", "file"));
      }
      if (!isEmpty(blackList)) {
        dispatch(createNewList(blackList.file.file, "BLACK", "file"));
      }
      if (!isEmpty(rivalList)) {
        dispatch(createNewList(rivalList.file.file, "CONCURRENT", "file"));
      }
      values.show_schedules = timeData.transformToDTO();

      values.date_start = moment(timeData.fromDate).format("YYYY-MM-DD");
      values.date_end = moment(timeData.toDate).format("YYYY-MM-DD");

      if (values.show_schedules
        && Object.keys(values.show_schedules).length === 0
        && Object.getPrototypeOf(values.show_schedules) === Object.prototype) {
        dispatch(enqueueSnackbar({
          message: "Заполните расписание показа",
          options: {
            variant: "error",
          },
        }))
        return;
      }

      let summ = 0;
      for (const day in values.show_schedules) {
        summ += values.show_schedules[day].length;
      }

      if (summ < 8) {
        const notifications = "В расписании должно быть не менее 8 часов показа. Сейчас отмечено " + summ
        dispatch(
          enqueueSnackbar({
            message: notifications,
            options: {
              variant: "error",
            },
          })
        );
        return;
      }
      const handleChangeTab = () => handleChange("event", "3")
      if (!DataEqual(data, values)) {
        await dispatch(
          updateCampaign(values, campaignID, "media_plan", handleChangeTab)
        );
      }
      return handleChangeTab()
    },
  });

  return (
    <div className="media-plan__wrapper">
      <div
        className="title-text"
        style={{
          marginBottom: "50px",
        }}
      >
        <h2>Медиа План</h2>
        {/* <p>3000+ Сегментов - Показывайте рекламу Только Тем, Кто Вам Нужен</p> */}
      </div>
      <form onSubmit={handleSubmit}>
        <Grid
          className="grid"
          container
          spacing={{ xs: 3, md: 3 }}
          columns={{ xs: 2, sm: 8, md: 12 }}
          rowSpacing={5}
          alignItems="center"
          justify="center"
        >
          <Grid item xs={12}>
            <div className="placement-section">
              <p className="placement-section__title">Параметры размещения</p>
              <p className="placement-section__subtitle">
                Управление белым, чёрным списком и сайтами конкурентов
              </p>
              {uploadErrors.whiteList || uploadErrors.blackList || uploadErrors.rivalList ? (
                <p className="placement-section__error">
                  {uploadErrors.whiteList || uploadErrors.blackList || uploadErrors.rivalList}
                </p>
              ) : null}
              <div className="placement-section__cards">
                <PlacementCard
                  title="Белый список"
                  description="Приоритетный список сайтов"
                  accent="white"
                  onClick={() => {
                    setModalType("white");
                    setOpenModal(true);
                  }}
                />
                <PlacementCard
                  title="Чёрный список"
                  description="Сайты, на которых показ рекламы запрещён"
                  accent="black"
                  onClick={() => {
                    setModalType("black");
                    setOpenModal(true);
                  }}
                />
                <PlacementCard
                  title="Сайты конкурентов"
                  description="Список сайтов конкурентов для таргета"
                  accent="concurrent"
                  onClick={() => {
                    setModalType("concurrent");
                    setOpenModal(true);
                  }}
                />
              </div>
            </div>
          </Grid>

          <Grid className="toggle" item xs={4}>
            <p style={{ marginTop: "15px", marginLeft: "10px" }}>
              Тип устройства*
            </p>
            <FormControl fullWidth sx={{ m: 1, width: "100%" }}>
              <FormGroup
                sx={{
                  marginLeft: "20px",
                  color: "black",
                }}
              >
                <div style={{ display: "flex", marginLeft: "-15px" }}>
                  <FormControlLabel
                    disabled={disable}
                    control={
                      <Switch
                        checked={values.enable_mobile}
                        onChange={(event) => setFieldValue("enable_mobile", event.target.checked)}
                      />
                    }
                    label="Мобильные"
                    name="enable_mobile"
                  />
                  <FormControlLabel
                    disabled={disable}
                    control={
                      <Switch
                        checked={values.enable_desktop}
                        onChange={(event) => setFieldValue("enable_desktop", event.target.checked)}
                      />
                    }
                    label="Десктопные"
                    name="enable_desktop"
                    sx={{ marginLeft: "10px" }}
                  />
                </div>
              </FormGroup>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={4}
            sx={{
              marginTop: "35px",
            }}
          ></Grid>
          <Grid
            container
            spacing={2}
            sx={{
              marginLeft: "25px",
              marginTop: "25px",
            }}
          >
            <RangeDaysPicker disabled={false} timeData={timeData} from={data?.date_start} to={data?.date_end} />
          </Grid>
          <Grid
            container
            sx={{
              marginTop: "50px",
              marginLeft: "25px",
            }}
          >
            <Grid className="back" item xs={4}>
              <Link to="/">
                <Button variant="outlined">Отмена</Button>
              </Link>
            </Grid>

            <Grid container item xs={8} justifyContent="flex-end">
              <TooltipedButton
                title="Не выбран тип устройства"
                isShowTitle={!values.enable_desktop && !values.enable_mobile}
                placement="top"
                renderButton={() => {
                  if (isSubmitting)
                    return (
                      <LoadingButton
                        loading={true}
                        loadingPosition="end"
                        endIcon={<SendIcon />}
                        variant="contained"
                      >
                        Сохранение
                      </LoadingButton>
                    );
                  return (
                    <div style={{ display: "flex", gap: "10px" }}>
                      <Button
                        variant="contained"
                        style={{ background: "#6c788f", color: "white" }}
                        endIcon={<RotateLeftIcon />}
                        disabled={!values.enable_desktop && !values.enable_mobile}
                        onClick={() => handleDatesClear()}
                      >
                        Сбросить
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={<SendIcon />}
                        disabled={!values.enable_desktop && !values.enable_mobile}
                        onClick={() => {
                          handleSubmit()
                          setIsSubmit(!isSubmit)
                        }}
                      >
                        Далее
                      </Button>
                    </div>
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
      <OpenModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        type={modalType}
        campaignId={campaignID}
      />
    </div>
  );
};

export default MediaPlan;
